import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { FAQS, PRE_APPOINTMENT } from "../data/content";
import { Download, ClipboardList, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Resources() {
  const navigate = useNavigate();
  return (
    <div data-testid="resources-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <span className="inline-block bg-[#E8DAB2]/60 text-slate-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
        Patient Resources
      </span>
      <h1 className="font-heading text-4xl sm:text-5xl text-slate-900 leading-tight">
        Everything you need before your visit
      </h1>

      {/* Pre-appointment instructions */}
      <section className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl border border-[#EDF1EF] p-7">
          <h2 className="font-heading text-xl text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#5E8B7E]" /> Pre-Appointment Instructions
          </h2>
          <ul className="mt-4 space-y-3">
            {PRE_APPOINTMENT.map((p, i) => (
              <li key={p} className="flex gap-3 text-sm text-slate-600">
                <span className="w-5 h-5 rounded-full bg-[#D9E4E1] text-[#4A7065] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-6">
          <div className="bg-[#D9E4E1]/40 rounded-2xl border border-[#D9E4E1] p-7">
            <h2 className="font-heading text-xl text-slate-800 flex items-center gap-2">
              <Download className="w-5 h-5 text-[#5E8B7E]" /> Patient Intake Form
            </h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Save time at the clinic — download, print and fill this form before your first visit.
              It covers your medical history, current medications, and consent.
            </p>
            <a href="/forms/patient-intake-form.pdf" download data-testid="download-intake-form-btn">
              <Button className="mt-4 rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">
                <Download className="w-4 h-4 mr-2" /> Download Intake Form (PDF)
              </Button>
            </a>
          </div>

          <div className="bg-white rounded-2xl border border-[#EDF1EF] p-7">
            <h2 className="font-heading text-xl text-slate-800 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#5E8B7E]" /> Your Privacy, Protected
            </h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              All patient data — registrations, appointments, uploaded documents and medical history
              — is transmitted over encrypted HTTPS and protected with strict role-based access.
              Only you and Dr. Prathusha can view your records. We comply with the Digital Personal
              Data Protection Act, 2023 and the Telemedicine Practice Guidelines, 2020.
            </p>
          </div>
        </div>
      </section>

      {/* Registration callout */}
      <section className="mt-10 bg-[#2C557E] rounded-2xl p-8 text-white flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex-1">
          <h2 className="font-heading text-2xl">New here? Register as a patient</h2>
          <p className="text-blue-100 text-sm mt-2">
            Only registered patients can book appointments. Registration takes two minutes — verify
            your email with an OTP and you're all set to book in-clinic or online consultations.
          </p>
        </div>
        <Button
          data-testid="resources-register-btn"
          onClick={() => navigate("/register")}
          className="rounded-full bg-[#E8DAB2] text-slate-800 hover:bg-[#dccd9e] px-8"
        >
          Register Now
        </Button>
      </section>

      {/* FAQs */}
      <section className="mt-14">
        <h2 className="font-heading text-3xl text-slate-900">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="mt-6 space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`faq-${i}`}
              className="bg-white rounded-2xl border border-[#EDF1EF] px-6 data-[state=open]:border-[#D9E4E1]"
            >
              <AccordionTrigger data-testid={`faq-item-${i}`} className="hover:no-underline text-left font-medium text-slate-800 py-4">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
